import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";
import { QueryClientProvider } from "react-query";
import socket from "./services/socket";
import CustomAppBar from "./components/AppBar";
import { HomePage } from "./pages";
// import { DrivePage } from "./pages/drive";
// import { DriveFolderPageV2 } from "./pages/drive/DriveFolder_v2";
import { LoginPage, LoginRedirectedPage } from "./pages/login";
import PrivateRoute from "./PrivateRoute";
import ReactNotifications from "react-notifications-component";
import "react-notifications-component/dist/theme.css";
import { TestPage } from "./pages/testpage";
// import { getLibraries, getLibraryStateIndex } from "./services/backend";
import { getCachedAuthInfo } from "./services/backend-api";
import { CacheKeyTypes, makeKey } from "./services/cache";
import { APP_VERSION, links } from "./constants";
import queryClient from "./services/query-client";
import ErrorBoundary from "./components/ErrorBoundary";
import { Changelog, trpcClient } from "./services/rpc-client";
import { GetLibraryStateIndex } from "@backend-ctrl/library.controller";
import { AuthContextProvider } from "./AuthContext";
import SwSetup from "./ServiceWorker";
import {
  FeedSkeleton,
  LibraryItemSkeleton,
  ListPageSkeleton,
  ListsPageSkeleton,
  PeoplePageSkeleton,
  PersonPageSkeleton,
} from "@components/Skeleton";
import { ThemeContextProvider } from "./context";
import { captureException } from "@sentry/react";
import { SnowTheme } from "@components/_Misc/SnowTheme"; // !XMAS

// import { persistQueryClient } from "react-query/persistQueryClient-experimental";
// import { createWebStoragePersistor } from "react-query/createWebStoragePersistor-experimental";

const ReactQueryDevtools = React.lazy(() => import("react-query/devtools").then((d) => ({ default: d.ReactQueryDevtools })));

const DriveFolderPageIndex = React.lazy(() => import("./pages/drive"));
const DriveFolderPageV2 = React.lazy(() => import("./pages/drive/DriveFolder_v2"));
const DriveItemPage = React.lazy(() => import("./pages/drive/DriveItem"));
const PeoplePage = React.lazy(() => import("./pages/person/PeoplePage"));
const PersonPage = React.lazy(() => import("./pages/person/PersonPage"));
const FeedPage = React.lazy(() => import("./pages/feed/FeedPage"));
const ChangelogModal = React.lazy(() => import("./components/Changelog/ChangelogModal"));
const PatreonConnectPage = React.lazy(() => import("./pages/auth/PatreonConnectPage"));
const ListsPage = React.lazy(() => import("./pages/list/ListsPage"));
const ListPage = React.lazy(() => import("./pages/list/ListPage"));

const ManageLibraryPage = React.lazy(() => import("./pages/library/Manage"));
const ManagePage = React.lazy(() => import("./pages/manage/ManagePage"));
const ManageImages = React.lazy(() => import("./pages/manage/ManageImagesPage"));
const ManageTitlesPage = React.lazy(() => import("./pages/manage/ManageTitlesPage"));
const ManageFeedbackPage = React.lazy(() => import("./pages/manage/ManageFeedbackPage"));
const EventsPage = React.lazy(() => import("./pages/manage/EventsPage"));
const SettingsPage = React.lazy(() => import("./pages/settings/SettingsPage"));
const CalendarPage = React.lazy(() => import("./pages/CalendarPage"));

let prefetchedAttempted = false;
const cachedAppVersion = localStorage.getItem("cachedAppVersion");
const versionMatches = APP_VERSION === cachedAppVersion;

// const localStoragePersistor = createWebStoragePersistor({ storage: window.localStorage });
// persistQueryClient({
//   queryClient,
//   buster: APP_VERSION,
//   persistor: localStoragePersistor,
//   // dehydrateOptions: { dehydrateMutations: false, shouldDehydrateQuery: (k) => k.queryKey === "list_of_persistable_keys" },
// });

const prefetch = async (includeChangelog?: boolean) => {
  const data = await trpcClient.query("prefetch", { changelog_version: includeChangelog ? APP_VERSION : undefined });
  trpcClient.query("prefetch", {});

  queryClient.setQueryData<GetLibraryStateIndex>(CacheKeyTypes.LibraryItemState, data.user_state);
  if (data.changelog) {
    queryClient.setQueryData<Changelog>(makeKey(CacheKeyTypes.ChangeLog, data.changelog.version), data.changelog);
  }
  // FIX: fix the type for this
  // for (const lib of data.libraries) {
  //   const http_path_name = lib.http_path_name;
  //   if (document.URL.includes(http_path_name)) continue;
  //   queryClient.setQueryData<GetLibrary>(makeKey(CacheKeyTypes.Library, http_path_name), lib);
  //   // const a: InferQueryOutput<'library'> = {maps: [{du}]}
  // }
};

function App() {
  const isMember = getCachedAuthInfo().is_member;
  if (!prefetchedAttempted && isMember) {
    prefetchedAttempted = true;
    prefetch(!versionMatches).catch((err) => {
      captureException(err, { extra: { at: "PREFETCH" } });

      // prefetchNotificationsStateFallback();
    });
    console.log("socket.connect");
    socket.connect();
  }

  document.getElementById("update")?.remove();
  document.getElementById("update_scr")?.remove();
  // useEffect(() => {
  //   (window as any).__loaded = 1;
  //   setTimeout(() => {
  //     document.getElementById("update")?.remove();
  //     document.getElementById("update_scr")?.remove();
  //   }, 3000);
  // }, []);

  // fix: will NOT with for pages with params, like /person/:id
  // we cna change Suspended to PrivateSuspended and take in the link instead of using window.location.pathname
  const skeletons: Record<string, (() => JSX.Element) | undefined> = {
    [links.people]: PeoplePageSkeleton,
    [links.feed]: FeedSkeleton,
    [links.lists]: ListsPageSkeleton,
  };

  const Suspensed: React.FC<{ C: any; S?: () => JSX.Element }> = ({ C, S }) => {
    const Skeleton = S || skeletons[window.location.pathname];
    const fallback = Skeleton ? <Skeleton /> : "";
    return (
      <ErrorBoundary fallback={""}>
        <Suspense fallback={fallback}>
          <C key={window.location.pathname} />
          {/* {fallback} */}
        </Suspense>
      </ErrorBoundary>
    );
  };

  return (
    <Router>
      {/* <ThemeProvider theme={dark}> */}
      <ThemeContextProvider>
        <AuthContextProvider>
          <QueryClientProvider client={queryClient}>
            <ReactNotifications />
            <RouteChangeListener />
            <SnowTheme />

            {/* ReactQuery devtools */}
            {import.meta.env.DEV && (
              <Suspense fallback="">
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}

            {isMember && <ChangelogDisplay />}
            {isMember && <SwSetup />}

            {/* 'hack' to add some height for appbar that's position fixed */}
            <div style={{ height: 56 }} />
            <CustomAppBar />

            <Switch>
              <Route path="/" exact component={HomePage} />
              <Route path="/login" exact component={LoginPage} />
              <Route path="/login/verify" exact component={LoginRedirectedPage} />
              <PrivateRoute path={links.folders} exact component={() => <Suspensed C={DriveFolderPageIndex} />} />
              <PrivateRoute path="/test" exact component={TestPage} />
              <PrivateRoute
                path={links.library_item.path}
                exact
                component={() => <Suspensed C={DriveItemPage} S={() => <LibraryItemSkeleton commentsLoading={true} />} />}
              />
              {/* <PrivateRoute
                path={links.library_item.path}
                exact
                component={() => <Suspensed C={DriveItemPage} S={() => <LibraryItemSkeleton commentsLoading={true} />} />}
              /> */}
              <PrivateRoute path={links.settings} exact component={() => <Suspensed C={SettingsPage} />} />
              <PrivateRoute
                path={links.person}
                exact
                component={() => <Suspensed C={PersonPage} S={PersonPageSkeleton} />}
              />
              <PrivateRoute path={links.people} exact component={() => <Suspensed C={PeoplePage} />} />
              <PrivateRoute path={links.folder} exact component={() => <Suspensed C={DriveFolderPageV2} />} />
              <PrivateRoute path={links.feed} exact component={() => <Suspensed C={FeedPage} />} />
              <PrivateRoute path={links.patreon_redirect} exact component={() => <Suspensed C={PatreonConnectPage} />} />
              <PrivateRoute path={links.list} exact component={() => <Suspensed C={ListPage} S={ListPageSkeleton} />} />
              <PrivateRoute path={links.lists} exact component={() => <Suspensed C={ListsPage} />} />
              <PrivateRoute path={links.calendar} exact component={() => <Suspensed C={CalendarPage} />} />

              {/* manager routes */}
              <PrivateRoute path={links.manage} exact component={() => <Suspensed C={ManagePage} />} />
              <PrivateRoute path={links.manage_images} exact component={() => <Suspensed C={ManageImages} />} />

              {/* stories */}
              <PrivateRoute path={links.manage_stories} exact component={() => <Suspensed C={ManageLibraryPage} />} />

              {/* title-items (auto-matcher) */}
              <PrivateRoute
                path={links.manage_titleitem_automatches}
                exact
                component={() => <Suspensed C={ManageTitlesPage} />}
              />

              {/* manage feedback */}
              <PrivateRoute path={links.manage_feedback} exact component={() => <Suspensed C={ManageFeedbackPage} />} />

              {/* actions / logs */}
              <PrivateRoute path={links.manage_events} exact component={() => <Suspensed C={EventsPage} />} />

              {/* <PrivateRoute path={links.manage_changelog} exact component={() => <Suspensed C={ManageLibraryPage} />} /> */}
              {/* <PrivateRoute path={links.manage_titles} exact component={() => <Suspensed C={RemoteTitles} />} /> */}
              {/* <PrivateRoute path={links.feedback} exact component={() => <Suspensed C={FeedbackPage} />} /> */}
              <Route component={NothingToSeeHere} />
            </Switch>
          </QueryClientProvider>
        </AuthContextProvider>
      </ThemeContextProvider>
      {/* </ThemeProvider> */}
    </Router>
  );
}

function RouteChangeListener() {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (history.action !== "PUSH") return;
    window.scrollTo({ behavior: "auto", top: 0 });
  }, [location.pathname, history.action]);

  return null;
}

function NothingToSeeHere() {
  return <Redirect to={{ pathname: "/" }} />;
}

/** Renders the changelog if cached version doesn't match current version */
function ChangelogDisplay() {
  const [open, setOpen] = useState(!versionMatches);
  if (versionMatches || !open) return <></>;

  return (
    <Suspense fallback="">
      <ChangelogModal open={open} setOpen={setOpen} shouldFetch={true} />
    </Suspense>
  );
}

export default App;
